/**
 * desktopChatStore — manages the Desktop-level state of the SpaceChat panel,
 * including intake mode and whether the chat is docked or free-floating (detached).
 *
 * Intake mode is the experience shown on a brand-new Desktop: the chat renders
 * centered on screen, larger than a normal floating panel, with the regular
 * chat header and work-shape prompting. When the user sends their first
 * message, endIntake() is called and the panel shrinks back down and docks
 * to its corner.
 *
 * Intake is keyed to a specific desktop id (`intakeDesktopId`), not a global
 * boolean. Consumers derive `intakeActive` as `intakeDesktopId === <live active
 * desktop id>`. This makes intake self-clearing: switching to any other desktop
 * automatically drops out of intake (the active id no longer matches) with no
 * forced state mutation, eliminating the ordering races between startIntake()
 * and setActiveDesktop() that previously left the chat stuck in intake.
 *
 * Detached mode lets the user pop the chat out into a freely draggable + resizable
 * window anywhere on the Desktop. The rect is persisted in localStorage so it
 * survives page reloads.
 *
 * Per-desktop state: presentation and the detached rect are persisted PER desktop
 * (keyed by the active desktop/space id), so each desktop keeps its own chat
 * placement and size. `setActiveDesktop(id)` is called whenever the active desktop
 * changes; it loads that desktop's saved state into the live store fields.
 */

import { create } from 'zustand';
import { IS_DEV } from '../utils/devMode';
import { getSystemBarInset } from '../utils/systemBarInset';
import { clampDetachedRect } from '../utils/chatPanelSize';
import { useOnboardingStore } from './onboardingStore';

export type ChatPanelPresentation = 'docked' | 'detached' | 'embedded';

/**
 * Distinguishes what intake mode is for, since both share the same centered/
 * enlarged floating-chat visual treatment but drive different downstream
 * behavior (River welcome message + persona chips for 'new_desktop'; hidden
 * chrome + persona buttons + large-text messages for 'onboarding' — see
 * DesktopShell, SpaceChat, and onboardingStore).
 */
export type IntakeKind = 'new_desktop' | 'onboarding';

export interface DetachedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const LS_RECT_PREFIX = 'riverDesktopChatRect';
const LS_PRESENTATION_PREFIX = 'riverDesktopChatPresentation';
const DEFAULT_DETACHED_WIDTH = 540;
const DEFAULT_DETACHED_HEIGHT = 640;
// Intake (brand-new Desktop) opens larger than a normal detached panel so the
// "what do you want to work on?" prompt feels front-and-center. It shrinks
// back to the regular docked size the moment the first message is sent.
const INTAKE_DETACHED_WIDTH = 640;
const INTAKE_DETACHED_HEIGHT = 760;
// Cap the intake panel's height to a fraction of the usable viewport height
// (below the Desktop systembar) rather than always requesting the full
// INTAKE_DETACHED_HEIGHT. Without this, `clampDetachedRect` only shrinks the
// rect down to whatever fits on-screen, so on shorter/laptop viewports the
// panel ends up wedged edge-to-edge with the systembar and the bottom of the
// window — i.e. it visually "fills the entire vertical". Reserving a margin
// keeps it looking like a centered floating panel at any viewport height.
const INTAKE_MAX_HEIGHT_RATIO = 0.8;
const DEFAULT_PRESENTATION: ChatPanelPresentation = 'embedded';

// Per-desktop localStorage keys. When no desktop id is known yet (initial module
// load, before the first setActiveDesktop), we fall back to the legacy global
// keys so an in-progress session keeps its placement until the id resolves.
function rectKey(desktopId: string | null): string {
  return desktopId ? `${LS_RECT_PREFIX}:${desktopId}` : LS_RECT_PREFIX;
}
function presentationKey(desktopId: string | null): string {
  return desktopId ? `${LS_PRESENTATION_PREFIX}:${desktopId}` : LS_PRESENTATION_PREFIX;
}

function loadRect(desktopId: string | null): DetachedRect | null {
  try {
    const raw = localStorage.getItem(rectKey(desktopId));
    if (!raw) return null;
    const rect = JSON.parse(raw) as DetachedRect;
    // Reject implausibly large saved rects (e.g. polluted by a prior fullscreen
    // state) so the floating panel never opens covering the whole viewport.
    if (rect.width > window.innerWidth * 0.9 || rect.height > window.innerHeight * 0.9) {
      return null;
    }
    // Clamp into the current viewport so the panel is never hidden under the
    // Desktop systembar, or left stranded off-screen if the OS window was
    // resized since this rect was last saved.
    return clampDetachedRect(rect, getSystemBarInset());
  } catch { /* ignore */ }
  return null;
}

function saveRect(desktopId: string | null, rect: DetachedRect): void {
  try {
    localStorage.setItem(rectKey(desktopId), JSON.stringify(rect));
  } catch { /* ignore */ }
}

function loadPresentation(desktopId: string | null): ChatPanelPresentation {
  try {
    const raw = localStorage.getItem(presentationKey(desktopId));
    if (raw === 'docked' || raw === 'detached' || raw === 'embedded') return raw;
  } catch { /* ignore */ }
  return DEFAULT_PRESENTATION;
}

function savePresentation(desktopId: string | null, p: ChatPanelPresentation): void {
  try {
    localStorage.setItem(presentationKey(desktopId), p);
  } catch { /* ignore */ }
}

function centeredRect(width = DEFAULT_DETACHED_WIDTH, height = DEFAULT_DETACHED_HEIGHT): DetachedRect {
  const x = Math.max(0, Math.round((window.innerWidth - width) / 2));
  const y = Math.max(0, Math.round((window.innerHeight - height) / 2));
  // Clamp so a short viewport never centers the panel under the Desktop
  // systembar (plain `Math.max(0, …)` above isn't enough — 0 is still under
  // the bar).
  return clampDetachedRect({ x, y, width, height }, getSystemBarInset());
}

interface DesktopChatState {
  /** Id of the desktop (space) whose chat state is currently live in the store. */
  activeDesktopId: string | null;
  presentation: ChatPanelPresentation;
  /** Desktop id currently in first-run intake, or null. Intake is active only
   *  while this equals the live active desktop id. */
  intakeDesktopId: string | null;
  /** What the current intake is for. Only meaningful while intakeDesktopId is set. */
  intakeKind: IntakeKind | null;
  detachedRect: DetachedRect | null;

  /** Switch the live chat state to a given desktop, loading its persisted placement. */
  setActiveDesktop: (desktopId: string) => void;
  startIntake: (desktopId: string, kind?: IntakeKind) => void;
  endIntake: () => void;
  setPresentation: (p: ChatPanelPresentation) => void;
  togglePresentation: () => void;
  setDetachedRect: (rect: DetachedRect) => void;
  /** Clears the live desktop binding (activeDesktopId + any in-progress intake) so the
   *  next setActiveDesktop() call always re-evaluates from scratch instead of
   *  short-circuiting on a stale id match. Called from spaceStore.cleanup() when
   *  leaving a Desktop entirely (e.g. back to Home) — without this, abandoning a
   *  Desktop mid-intake and later reopening it normally (not via the intake nav
   *  path) would find activeDesktopId/intakeDesktopId still pointing at it and
   *  incorrectly re-render it centered in intake mode. */
  reset: () => void;
}

// Restore presentation from the legacy global key on module init so the value is
// ready before the first render. Once setActiveDesktop fires with a real desktop
// id, the per-desktop value takes over. detachedRect is loaded lazily when
// entering detached mode.
const initialPresentation = loadPresentation(null);

export const useDesktopChatStore = create<DesktopChatState>((set, get) => ({
  activeDesktopId: null,
  presentation: initialPresentation,
  intakeDesktopId: null,
  intakeKind: null,
  // If we're restoring detached mode, pre-load the rect so the panel positions
  // correctly on the first render without a flicker. Fall back to a centered rect
  // when nothing valid is saved, otherwise the detached wrapper would render with
  // no inline geometry and collapse to its CSS min-size in the top-left corner.
  detachedRect: initialPresentation === 'detached' ? (loadRect(null) ?? centeredRect()) : null,

  setActiveDesktop: (desktopId) => {
    if (!desktopId || desktopId === get().activeDesktopId) return;

    // In-chat onboarding runs `chatOnboardingStage` in a desktop-agnostic
    // global store (see onboardingStore.ts), unlike intakeDesktopId below
    // which is inherently desktop-scoped and self-clears the moment
    // activeDesktopId no longer matches it. Nothing clears chatOnboardingStage
    // on its own, so explicitly clear it here on any genuine desktop switch —
    // otherwise hidden chrome / large text leaks onto whatever desktop the
    // user navigates to next.
    //
    // This has to live here rather than in DesktopChatPanel (the previous
    // location, via a `useRef` diff) because `/desktops/:id` is keyed by id —
    // DesktopChatPanel fully unmounts and remounts on every switch, so a
    // React ref always reset to the *current* id on its very first post-switch
    // render and could never detect a switch. This store's own
    // `activeDesktopId` field is Zustand state and survives that remount, so
    // the comparison has to happen here instead.
    //
    // Deliberately only clears the flag itself — NOT the full
    // `endChatOnboarding()` helper, which also calls this store's own
    // `endIntake()`. That's correct when called from inside a live onboarding
    // desktop's own chat (chip click, skip link, ...), but here `desktopId` is
    // the desktop being switched TO, which may have already legitimately
    // started its own unrelated New-Desktop intake (see `startIntake` below) —
    // calling `endIntake()` here would wipe that out from underneath it.
    const prevActiveDesktopId = get().activeDesktopId;
    if (
      prevActiveDesktopId &&
      prevActiveDesktopId !== desktopId &&
      useOnboardingStore.getState().chatOnboardingStage !== null
    ) {
      useOnboardingStore.getState().setChatOnboardingStage(null);
    }

    // Activating the desktop that is mid-intake: keep its transient
    // centered-detached layout (set by startIntake). Loading the persisted
    // placement here would clobber the intake surface, so only sync the id.
    if (desktopId === get().intakeDesktopId) {
      if (IS_DEV) console.log('[CHAT_DETACH] setActiveDesktop (intake desktop)', { desktopId });
      set({ activeDesktopId: desktopId });
      return;
    }
    const presentation = loadPresentation(desktopId);
    const detachedRect = presentation === 'detached' ? (loadRect(desktopId) ?? centeredRect()) : null;
    if (IS_DEV) {
      console.log('[CHAT_DETACH] setActiveDesktop', { desktopId, presentation, detachedRect });
    }
    set({ activeDesktopId: desktopId, presentation, detachedRect });
  },

  startIntake: (desktopId, kind = 'new_desktop') => {
    // Intake uses a temporary centered-detached state; we do NOT persist this
    // so a reload during intake just opens the normal docked panel. Keying it to
    // the desktop id means switching away naturally exits intake.
    const usableHeight = Math.max(0, window.innerHeight - getSystemBarInset());
    const height = Math.min(INTAKE_DETACHED_HEIGHT, Math.round(usableHeight * INTAKE_MAX_HEIGHT_RATIO));
    const rect = centeredRect(INTAKE_DETACHED_WIDTH, height);
    if (IS_DEV) {
      console.log('[DESKTOP_INTAKE] startIntake', { desktopId, kind, rect });
      (window as any).__desktopIntake = { intakeDesktopId: desktopId, kind, rect, ts: Date.now() };
    }
    set({ intakeDesktopId: desktopId, intakeKind: kind, presentation: 'detached', detachedRect: rect });
  },

  endIntake: () => {
    if (IS_DEV) {
      const started = (window as any).__desktopIntake?.ts;
      const elapsedMs = started ? Date.now() - started : null;
      console.log('[DESKTOP_INTAKE] endIntake — docking chat', { elapsedMs });
      (window as any).__desktopIntake = { intakeDesktopId: null, elapsedMs };
    }
    savePresentation(get().activeDesktopId, 'docked');
    set({ intakeDesktopId: null, intakeKind: null, presentation: 'docked', detachedRect: null });
  },

  setPresentation: (p) => {
    const desktopId = get().activeDesktopId;
    if (IS_DEV) {
      console.log('[CHAT_DETACH] setPresentation', { p, desktopId });
      (window as any).__chatDetach = { presentation: p, detachedRect: get().detachedRect };
    }
    savePresentation(desktopId, p);
    if (p === 'detached' && !get().detachedRect) {
      const saved = loadRect(desktopId);
      set({ presentation: p, detachedRect: saved ?? centeredRect() });
    } else {
      set({ presentation: p });
    }
  },

  togglePresentation: () => {
    const { presentation, detachedRect, activeDesktopId } = get();
    if (presentation === 'docked') {
      const saved = loadRect(activeDesktopId);
      const rect = saved ?? centeredRect();
      if (IS_DEV) {
        console.log('[CHAT_DETACH] detaching', { rect });
        (window as any).__chatDetach = { presentation: 'detached', detachedRect: rect };
      }
      savePresentation(activeDesktopId, 'detached');
      set({ presentation: 'detached', detachedRect: rect });
    } else {
      if (IS_DEV) {
        console.log('[CHAT_DETACH] docking');
        (window as any).__chatDetach = { presentation: 'docked', detachedRect };
      }
      savePresentation(activeDesktopId, 'docked');
      set({ presentation: 'docked' });
    }
  },

  setDetachedRect: (rect) => {
    saveRect(get().activeDesktopId, rect);
    if (IS_DEV) {
      console.log('[CHAT_DETACH] setDetachedRect', rect);
      (window as any).__chatDetach = { presentation: 'detached', detachedRect: rect };
    }
    set({ detachedRect: rect });
  },

  reset: () => {
    if (IS_DEV) console.log('[DESKTOP_INTAKE] desktopChatStore reset (leaving Desktop)');
    set({ activeDesktopId: null, intakeDesktopId: null, intakeKind: null });
  },
}));
